/** Same contract as the desktop model menu. A catalog with both routes stays one scrollable list. */
export type RouteTab = 'local' | 'cloud'

export interface BrowseItem {
  key: string
  label: string
  route: RouteTab
  selected: boolean
  disabled?: boolean
  hint?: string
  group?: string
  handoff?: boolean
  title?: string
}

export interface BrowseSection {
  id: RouteTab
  label: 'Local' | 'Cloud'
  items: BrowseItem[]
  empty: string
}

export interface BrowseCatalog {
  continuous: true
  query: string
  sections: BrowseSection[]
  selectedKey: string | null
}

export interface SectionOffset {
  id: RouteTab
  top: number
}

const LABEL: Record<RouteTab, 'Local' | 'Cloud'> = { local: 'Local', cloud: 'Cloud' }

const matches = (item: BrowseItem, query: string) => {
  if (!query) return true
  const hay = `${item.label} ${item.group ?? ''} ${item.hint ?? ''} ${item.title ?? ''}`.toLowerCase()
  return hay.includes(query)
}

export function browseCatalog(items: BrowseItem[], query: string, routes?: RouteTab[]): BrowseCatalog {
  const needle = query.trim().toLowerCase()
  const order = routes ?? [...new Set(items.map(item => item.route))]
  return {
    continuous: true,
    query,
    selectedKey: items.find(item => item.selected)?.key ?? null,
    sections: order.map(id => ({
      id,
      label: LABEL[id],
      items: items.filter(item => item.route === id && matches(item, needle)),
      empty: needle ? `No ${LABEL[id].toLowerCase()} models match` : `No ${LABEL[id].toLowerCase()} models`,
    })),
  }
}

export function activeSection(scrollTop: number, offsets: SectionOffset[]): RouteTab {
  let current = offsets[0]?.id ?? 'local'
  for (const section of offsets) {
    if (scrollTop + 1 >= section.top) current = section.id
  }
  return current
}

export function tabIntent(tab: RouteTab, offsets: SectionOffset[], selectedKey: string | null) {
  return { scrollTop: offsets.find(section => section.id === tab)?.top ?? 0, selectedKey }
}

export function committedKey(item: BrowseItem | null | undefined, explicit: boolean): string | null {
  if (!explicit || !item || item.disabled) return null
  return item.key
}

export function tabAfterKey(current: RouteTab, key: string, order: RouteTab[]): RouteTab {
  const index = Math.max(0, order.indexOf(current))
  if (key === 'ArrowRight') return order[(index + 1) % order.length] ?? current
  if (key === 'ArrowLeft') return order[(index - 1 + order.length) % order.length] ?? current
  return current
}
